<script setup lang="ts">
import { pb } from "@/utils/PocketBaseAdapter";
import { usePriorityBooking } from "@/store/usePriorityBookings";
import { useEventStore } from "@/store/useEventStore";
import { useCalendarStore } from "@/store/useCalendarStore";
import { useUserStore } from "@/utils/UserStore";
import { useUserRole } from "@/utils/useUserRole";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { useListStore } from "@/store/listStore";
import { moment } from "@/utils/useTimeZone";
import isEmpty from "lodash/isEmpty";

// Interface
interface Area {
  location: number;
}

interface Location {
  id: number;
  name: string;
}

// store
const priorityBooking = usePriorityBooking();
const events = useEventStore();
const calendar = useCalendarStore();
const userStore = useUserStore();
const getSetupStore = useGlobalSetupStore();
const userRole = useUserRole();
const list = useListStore();

const { handleEventEdit } = events;
const { deleteBooking, getUserList } = calendar;
const { getListArea, fetchActivities } = list;

const { getPriorityBookings } = priorityBooking;
const { getLocationList } = list;
const { priorityBookings, isLoading, isConfirmEditBookingDialog } = storeToRefs(priorityBooking);
const {
  addEditEventDialog,
  eventData,
  recurringDetails: eventRecurringDetails,
  confirmEditDialog,
  selectedEvent,
} = storeToRefs(events);
const {
  usersList,
  recurringDetails,
  isBookingStepperVisible,
  editBookingData,
} = storeToRefs(calendar);
const { currentLocation } = storeToRefs(userStore);
const { globalSetup } = storeToRefs(getSetupStore);
const { permissionListMap, userInfo, rolesList } = storeToRefs(userRole);
const { listArea, activityList, locationList } = storeToRefs(list);

// Data
const recurringConfig = ref<any>("");
const viewQR = ref(false)
const openViewOnlyWindow = ref(false)
const viewOnlyWindowBooking = ref<any>({});

// computed
const getMyRole = computed(() => {
  return rolesList?.value?.filter((role: any) =>
    userInfo.value?.roles?.includes(role.id),
  );
});

const getLocation = computed(() => (area: Area) => {
  const locations = locationList.value || [];
  const location = locations.find((loc: Location) => loc.id === area.location);
  return location?.name || "";
});

const userId = computed(() => pb.authStore.model?.id);

const getDay = computed(() => {
  const today = moment().startOf("day");
  const tomorrow = moment(today).add(1, "day");

  return (dateTime: string) => {
    const bookingDate = moment(dateTime).startOf("day");

    if (bookingDate.isSame(today)) {
      return "Today";
    } else if (bookingDate.isSame(tomorrow)) {
      return "Tomorrow";
    }
    return bookingDate.format("dddd, DD-MM");
  };
});

// Methods
const openQR = (booking: any) => {
  viewOnlyWindowBooking.value = booking
  viewQR.value = true
}

const handleEdit = async (booking: any) => {
  // Navigate to booking edit page
  if (booking.expand?.area?.location && booking.expand.area.location != userStore.currentLocation){
    // Get all Users in Booking
    booking.members = await pb.collection("bookings").getOne(booking.id, {expand:'users, options',
      fields:'expand.users.firstName, expand.users.lastName, expand.users.email, expand.users.id, expand.users.mobileNumber, expand.options.id, expand.options.name'})
    viewOnlyWindowBooking.value = booking
    openViewOnlyWindow.value = true
    return
  }
  const {
    recurringBookingDetails,
    eventDetails,
    recurringEventDetails,
    expand,
  } = booking;


  // manage recurring booking dialog
  const baseBookingData = {
    ...booking,
    selectedOptions: booking?.options || [],
    startDate: booking?.startTime,
    docketId: expand?.docketLine?.docket,
  };

  if (!isEmpty(recurringEventDetails)) {
    // Manage recurring event dialog
    const { id, events, frequency, interval, days, endDate, expand } =
      recurringEventDetails;

    const eventData = expand?.events?.find((event: any) =>
      event.expand?.bookings?.some((book: any) => book.id === booking?.id),
    );

    if (eventData) {
      selectedEvent.value = {
        ...eventData,
        recurring: {
          recurringId: id,
          events,
          frequency,
          interval,
          days,
          endDate,
        },
      };
      eventRecurringDetails.value = {
        isEditAll: false,
        recurringId: id,
        events,
      };
      isConfirmEditBookingDialog.value = true; // Show warning dialog
      confirmEditDialog.value = {
        type: "Event",
        title: "Edit Recurring Event",
        alertMessage:
          "This is one event in a series. What would you like to do?",
      };
    }
    return;
  }
  if (!isEmpty(eventDetails)) {
    // Manage event dialog
    confirmEditDialog.value = {
      type: "Event",
      title: "Edit Event",
      alertMessage: "Edit details for this event.",
    };
    handleEventEdit(eventDetails);
    return;
  }
  if (!isEmpty(recurringBookingDetails)) {
    const { interval, days, frequency, endDate } = recurringBookingDetails;
    editBookingData.value = {
      ...baseBookingData,
      recurring: { interval, days, frequency, endDate },
    };
    recurringConfig.value = recurringBookingDetails;
    isConfirmEditBookingDialog.value = true;
    confirmEditDialog.value = {
      type: "Booking",
      title: "Edit Recurring Booking",
      alertMessage:
        "This is one booking in a series. What would you like to do?",
    };
    return;
  }

  // Normal booking edit
  editBookingData.value = baseBookingData;
  isBookingStepperVisible.value = true;
};

const closeDialog = (value: boolean) => {
  isBookingStepperVisible.value = value;
  editBookingData.value = {};
};

const handleBookingEdit = (editType: string) => {
  /**
   * Handles the editing of a booking, recurring booking, or event.
   *
   * @param {string} editType - The type of edit to be performed. If 'ALL', all related bookings will be edited.
   *
   * @returns {void}
   */
  recurringDetails.value = {
    isEditAll: editType && editType === "ALL" ? true : false,
    recurringId: recurringConfig?.value?.id,
    bookings: recurringConfig?.value?.bookings,
  };

  isBookingStepperVisible.value = true;
  isConfirmEditBookingDialog.value = false;
};

watch(currentLocation, async (newVal) => {
  if (newVal) {
    await fetchActivities();
  }
});

// Hooks
onMounted(async () => {
  isBookingStepperVisible.value = false;
  await getLocationList();
  await getListArea();
  await getPriorityBookings(userId.value);
  if (currentLocation.value) await fetchActivities();
});
</script>

<template>
  <v-dialog v-model="openViewOnlyWindow" persistant max-width="900">
    <VCard height="auto" flat>
      <div class="d-flex align-center justify-space-between bg-dialogHeaderColor">

        <!-- 👉 Header -->
        <VCardTitle>
          {{viewOnlyWindowBooking?.expand?.activity?.name}} Booking Details
        </VCardTitle>

        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="openViewOnlyWindow = false"
        />
      </div>

      <VCardText class="text-center">
        <BookingOverview
          v-if="openViewOnlyWindow"
          :booking="viewOnlyWindowBooking"
          role=""
          :activity="viewOnlyWindowBooking?.expand?.activity"
          :areaList="listArea"
          :memberList="usersList"
          :products="viewOnlyWindowBooking?.members.expand?.options"
          :canMessage="false"
          :haveEditAccess="false"
          :haveCreateAccess="false"
          :debugUser="null"
        />
      </VCardText>
      <VCardSubtitle
        v-if="viewOnlyWindowBooking?.status !== 'futureBooking'"
        class="pa-5 pb-0 font-weight-medium">
        Options limited as booking {{ viewOnlyWindowBooking.status === "completed" ? "completed" : "started" }}
      </VCardSubtitle>
    </VCard>
  </v-dialog>

  <VCard
    v-if="priorityBookings.length"
    class="bookings-container"
  >
    <VCardTitle class="title">Upcoming bookings</VCardTitle>

    <VRow v-if="isLoading">
      <VCol v-for="n in 2" :key="n" cols="12" md="6" lg="3">
        <div class="booking-skeleton">
          <VCard class="booking-card skeleton animated-shimmer">
            <VCardText>
              <div class="skeleton-header shimmer"></div>
              <div class="skeleton-chips">
                <div class="skeleton-chip shimmer"></div>
                <div class="skeleton-chip shimmer"></div>
              </div>
              <div class="skeleton-details">
                <div class="skeleton-detail shimmer"></div>
              </div>
            </VCardText>
          </VCard>
        </div>
      </VCol>
    </VRow>

    <VRow v-else>
      <VCol
        v-for="booking in priorityBookings"
        :key="booking.id"
        cols="12"
        md="6"
        lg="3"
      >
        <VCard
          class="booking-card pb-0"
          @click="handleEdit(booking)"
          :class="{ 'bg-error': booking.due > 0 }"
        >
          <!-- Icon for recurring booking in top-right corner -->
          <VIcon
            v-if="
              !isEmpty(booking.recurringEventDetails) ||
              !isEmpty(booking.recurringBookingDetails)
            "
            class="top-right-icon recurring-icon"
            color="black"
          >
            mdi-calendar-outline
          </VIcon>

          <!-- Icon for event in top-left corner -->
          <VIcon
            v-if="!isEmpty(booking.eventDetails)"
            class="top-left-icon event-icon"
            color="black"
          >
            mdi-star-outline
          </VIcon>

          <VCardText class="card-content pt-2 d-flex justify-space-between">
            <div></div>
            <div class="text-center">
              <div class="booking-header d-flex justify-center align-center">
                <div class="activity" style="font-size: 22px;font-weight: bold;">
                  {{ moment(booking.startTime).format("h:mm A") }}
                </div>
              </div>
              <div class="booking-details d-flex justify-center align-center">
                <div class="mt-2">
                  <span class="info">
                    {{ getDay(booking.startTime) }}
                  </span>
                </div>
              </div>
              <div class="booking-details d-flex justify-center">
                <div class="mt-2">
                  <span class="info">
                    <VIcon icon="mdi-location" aria-label="Location" />
                    {{ getLocation(booking?.expand?.area) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="d-flex align-end align-center">
              <IconBtn
                aria-label="QR Code"
                @click.stop="openQR(booking)"
                color="black">
                <VIcon size="40" icon="mdi-qrcode" aria-label="QR Icon"/>
              </IconBtn>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Booking dialog -->
    <BookingStepper
      v-model:isDrawerOpen="isBookingStepperVisible"
      :booking="editBookingData"
      :recurring-details="recurringDetails"
      :activity-list="activityList"
      :area-list="listArea"
      :memberList="usersList"
      :location-list="locationList"
      :current-location="currentLocation"
      :current-logged-in-roles="getMyRole"
      :membership="userInfo?.expand?.membership"
      :user-id="userId"
      :permission="permissionListMap?.bookings ?? []"
      :coupon-value="globalSetup?.couponValue"
      @loadUsersList="getUserList()"
      @update:isDrawerOpen="closeDialog"
      @delete-booking="deleteBooking($event)"
      @addNewCardDialog=""
      @closeDialog="
        ((editBookingData = {}),
        (recurringDetails = {}),
        (isBookingStepperVisible = $event))
      "
      @reloadBookingList="getPriorityBookings(userId)"
    />

    <!-- Event dialog -->
    <AddEditEvents
      :is-drawer-open="addEditEventDialog"
      :event="eventData"
      :recurring-details="eventRecurringDetails"
      :activity-list="activityList"
      :area-list="listArea"
      :memberList="usersList"
      :location-list="locationList"
      :current-location="currentLocation"
      :area-plural="globalSetup?.areaPlural || 'Bays'"
      :area-singular="globalSetup?.areaSingular || 'Bay'"
      :current-logged-in-roles="getMyRole"
      :membership="userInfo?.expand?.membership"
      :user-id="userId"
      :permission="permissionListMap?.bookings"
      :coupon-value="globalSetup?.couponValue"
      @closeDialog="addEditEventDialog = false"
    />

    <!-- Suggestion edit dialog -->
    <EditConfirmBookingDialog
      :is-visible="isConfirmEditBookingDialog"
      :title="confirmEditDialog?.title"
      :alertMessage="confirmEditDialog?.alertMessage"
      @confirmEdit="
        ($event) => {
          isConfirmEditBookingDialog = false;
          if (confirmEditDialog?.type === 'Event') {
            eventRecurringDetails = {
              ...eventRecurringDetails,
              isEditAll: $event === 'ALL' ? true : false,
            };
            handleEventEdit(selectedEvent);
          } else {
            handleBookingEdit($event);
          }
        }
      "
      @closeDialog="
        ((editBookingData = {}),
        (eventRecurringDetails = {}),
        (isConfirmEditBookingDialog = $event))
      "
    />

    <!-- QR code Dialog -->
    <QrCode
      v-if="viewQR"
      :is-visible="viewQR"
      :qr-value="viewOnlyWindowBooking?.entryCode || ''"
      :booking="viewOnlyWindowBooking"
      @closeDialog="viewQR = false"
    />
  </VCard>
</template>

<style scoped lang="scss">
.bookings-container {
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 10%);
  color: var(--text-color);
  cursor: pointer;
}

.title {
  font-size: 23px;
  font-weight: 600;
  margin-block-end: 10px;
  text-align: center;
}

.loading-row {
  display: flex;
  align-items: center;
  justify-content: center;
  block-size: 100px;
}

.animated-shimmer {
  position: relative;
  overflow: hidden;
  background: var(--skeleton-bg);
}

.shimmer {
  animation: shimmer 1.5s infinite;
  background: var(--shimmer-bg);
  background-size: 200% 100%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

.booking-skeleton {
  padding: 8px;
  border-radius: 10px;
}

.skeleton-header {
  border-radius: 4px;
  background-color: var(--skeleton-bg);
  block-size: 15px;
  margin-block-end: 6px;
}

.skeleton-chips {
  display: flex;
  gap: 6px;
  margin-block-end: 6px;
}

.skeleton-chip {
  flex: 1;
  border-radius: 16px;
  background-color: var(--skeleton-bg);
  block-size: 10px;
}

.skeleton-details {
  line-height: 1.5;
}

.skeleton-detail {
  border-radius: 4px;
  background-color: var(--skeleton-bg);
  block-size: 15px;
  margin-block-end: 3px;
}

// Other styles remain unchanged

.booking-card {
  position: relative;
  overflow: hidden;
  padding: 10px;
  border-radius: 10px;
  background-color: rgb(var(--v-theme-dialogHeaderColor));
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease,
    background-color 0.3s ease;
}

.booking-card:hover {
  background-color: rgb(var(--v-theme-primary)) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 15%);
  transform: scale(1.05);
}

.top-right-icon {
  position: absolute;
  inset-block-start: 10px;
  inset-inline-end: 10px;
}

.top-left-icon {
  position: absolute;
  inset-block-start: 10px;
  inset-inline-start: 10px;
}

.recurring-icon, .event-icon {
  font-size: 24px;
}

.card-content {
  font-size: 19px;
}
</style>
